import { useState } from "react";
import { Ban, Unlock, Loader2, X, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { MonthCalendar } from "../../components/MonthCalendar";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });

function Legend({ mode }) {
  const items = [["bg-sky-500", "Ricevimento"], ["bg-rose-400", "Weekend"], ["bg-rose-600", "Festività"],
    ["bg-emerald-500", "Sbloccato"], ["bg-slate-800", "Bloccato da admin"]];
  if (mode === "block") items.push(["bg-blue-50 ring-2 ring-inset ring-blue-600", "Selezionato"]);
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5" data-testid="admin-calendar-legend">
      {items.map(([c, l]) => (
        <span key={l} className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className={`h-3 w-3 rounded ${c}`} /> {l}</span>
      ))}
    </div>
  );
}

function ConfirmBlock({ preview, onCancel, onConfirm }) {
  const n = preview?.appointments.length || 0;
  return (
    <AlertDialog open={!!preview} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="block-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Bloccare {preview?.dates.length} giorni?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p>Gli informatori non potranno prenotare in: <strong>{preview?.dates.length > 7 ? `dal ${fmt(preview.dates[0])} al ${fmt(preview.dates[preview.dates.length - 1])}` : preview?.dates.map(fmt).join(", ")}</strong>.</p>
              {n === 0 ? <p>Non ci sono appuntamenti in questi giorni.</p> : (
                <>
                  <p className="font-600 text-red-600">Verranno annullati {n === 1 ? "1 appuntamento già preso" : `${n} appuntamenti già presi`} (gli ISF e il medico saranno avvisati):</p>
                  <ul className="max-h-32 list-disc overflow-y-auto pl-5 text-xs" data-testid="block-affected-list">
                    {preview.appointments.map((a) => <li key={`${a.date}-${a.time}`}>{fmt(a.date)} ore {a.time} · {a.isf}</li>)}
                  </ul>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="block-cancel-btn">Annulla</AlertDialogCancel>
          <AlertDialogAction className="bg-slate-900 hover:bg-slate-800" onClick={onConfirm} data-testid="block-confirm-btn">
            Blocca giorni
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const MAX_RANGE_DAYS = 366;

function datesBetween(from, to) {
  const out = [];
  const d = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  while (d <= end && out.length < MAX_RANGE_DAYS) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function RangeBlock({ busy, onBlock }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const valid = from && to && from <= to;
  const submit = () => {
    const dates = datesBetween(from, to);
    if (dates.length === MAX_RANGE_DAYS) toast.warning(`Massimo ${MAX_RANGE_DAYS} giorni per volta`);
    onBlock(dates);
  };
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="range-block">
      <span className="w-full text-xs font-600 text-slate-700">Blocca un periodo</span>
      <label className="text-[11px] text-slate-500">Dal
        <Input type="date" min={today} value={from} onChange={(e) => { setFrom(e.target.value); if (!to || e.target.value > to) setTo(e.target.value); }}
          className="mt-0.5 h-8 w-40 bg-white text-xs" data-testid="range-from-input" />
      </label>
      <label className="text-[11px] text-slate-500">Al
        <Input type="date" min={from || today} value={to} onChange={(e) => setTo(e.target.value)}
          className="mt-0.5 h-8 w-40 bg-white text-xs" data-testid="range-to-input" />
      </label>
      <Button size="sm" className="h-8 gap-1 bg-slate-900 hover:bg-slate-800" disabled={!valid || busy} onClick={submit} data-testid="range-block-btn">
        <CalendarRange className="h-3.5 w-3.5" /> Blocca periodo{valid ? ` (${datesBetween(from, to).length} giorni)` : ""}
      </Button>
    </div>
  );
}

export function MedicoDaysPanel({ medicoId, form, setForm, holidays, onAudit }) {
  const [mode, setMode] = useState("block");
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const blocked = form.admin_blocked_days;
  const selBlocked = selected.filter((d) => blocked.includes(d));
  const selFree = selected.filter((d) => !blocked.includes(d));

  const toggleUnlock = async (dIso) => {
    const isUnlocked = form.unlocked_days.includes(dIso);
    try {
      const { data: res } = await api.post(`/admin/medici/${medicoId}/unlock-days`, { dates: [dIso], unlock: !isUnlocked });
      setForm({ ...form, unlocked_days: res.unlocked_days });
      toast.success(isUnlocked ? "Giorno ribloccato" : "Giorno sbloccato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onSelect = (d) => {
    if (mode === "unlock") return toggleUnlock(d);
    setSelected(selected.includes(d) ? selected.filter((x) => x !== d) : [...selected, d].sort());
  };

  const askBlock = async (dates = selFree) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/medici/${medicoId}/block-days/preview`, { dates, block: true });
      setPreview({ dates, appointments: data.appointments });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const apply = async (dates, block) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/medici/${medicoId}/block-days`, { dates, block });
      setForm({ ...form, admin_blocked_days: data.admin_blocked_days });
      onAudit(data.audit_logs);
      setSelected(selected.filter((d) => !dates.includes(d)));
      toast.success(block ? `${dates.length} giorni bloccati · ${data.cancelled} appuntamenti annullati` : "Divieto rimosso");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setPreview(null);
    setBusy(false);
  };

  const getMarker = (d) => {
    if (mode === "block" && selected.includes(d)) return "selected";
    if (blocked.includes(d)) return "blocked";
    if (form.unlocked_days.includes(d)) return "unlocked";
    const wd = (new Date(d + "T00:00:00").getDay() + 6) % 7;
    if (holidays[d]) return "holiday";
    if (wd >= 5) return "weekend";
    if (((form.weekly_slots || {})[String(wd)] || []).length > 0) return "reception";
    return null;
  };

  return (
    <div data-testid="medico-days-panel">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <Label className="block">Calendario del medico</Label>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-600">
          <button type="button" onClick={() => setMode("block")} data-testid="mode-block-btn"
            className={`flex items-center gap-1 rounded-md px-3 py-1.5 transition-colors ${mode === "block" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            <Ban className="h-3.5 w-3.5" /> Blocca giorni
          </button>
          <button type="button" onClick={() => { setMode("unlock"); setSelected([]); }} data-testid="mode-unlock-btn"
            className={`flex items-center gap-1 rounded-md px-3 py-1.5 transition-colors ${mode === "unlock" ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
            <Unlock className="h-3.5 w-3.5" /> Sblocca festivi / weekend
          </button>
        </div>
      </div>
      <p className="mb-2 text-xs text-slate-500">
        {mode === "block"
          ? "Seleziona uno o più giorni: potrai vietare le prenotazioni degli informatori. Gli appuntamenti già presi in quei giorni verranno annullati."
          : "Clicca un giorno festivo o del weekend per sbloccarlo su richiesta del medico."}
      </p>
      <MonthCalendar getMarker={getMarker} onSelect={onSelect} />
      <Legend mode={mode} />
      {mode === "block" && <RangeBlock busy={busy} onBlock={askBlock} />}
      {mode === "block" && selected.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5" data-testid="block-actions">
          <span className="text-xs font-600 text-blue-900" data-testid="block-selected-count">{selected.length} giorni selezionati</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" className="h-8 gap-1 text-slate-600" onClick={() => setSelected([])} data-testid="block-clear-btn">
              <X className="h-3.5 w-3.5" /> Deseleziona
            </Button>
            {selBlocked.length > 0 && (
              <Button size="sm" variant="outline" className="h-8 gap-1" disabled={busy} onClick={() => apply(selBlocked, false)} data-testid="unblock-selected-btn">
                <Unlock className="h-3.5 w-3.5" /> Rimuovi divieto ({selBlocked.length})
              </Button>
            )}
            {selFree.length > 0 && (
              <Button size="sm" className="h-8 gap-1 bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={() => askBlock()} data-testid="block-selected-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Blocca ({selFree.length})
              </Button>
            )}
          </div>
        </div>
      )}
      <ConfirmBlock preview={preview} onCancel={() => setPreview(null)} onConfirm={() => apply(preview.dates, true)} />
    </div>
  );
}
